import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
  Code as CodeIcon,
  Cloud as CloudIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const DevTechnologyContracts: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/pi-planning');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to PI Planning Board"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Dev Technology Group - CBP Contracts & Programs
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Prime Contractor for ACE, ATAP, Cargo Systems, and Analytics Platforms
          </Typography>
        </Box>
      </Box>

      {/* Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Contractor Overview
          </Typography>
          <Typography variant="body1" paragraph>
            Dev Technology Group is a leading prime contractor supporting several major CBP program contracts, with 
            focus on application development, modernization, and analytics platforms for trade and cargo management, 
            as well as identity and biometrics. As an SBA-approved mentor, Dev Technology delivers Agile development, 
            DevSecOps, legacy modernization, data management, and advanced analytics capabilities across CBP's mission-critical 
            systems including ACE, ATAP, and cargo services platforms.
          </Typography>
        </CardContent>
      </Card>

      {/* Contract Portfolio Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Active Contract Portfolio
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #003366' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                $364M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Contract Value
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Active CBP contracts
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                4
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Major Contracts
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Prime contractor awards
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                3
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Core Systems
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                ACE, ATAP, Cargo
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                2025
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Recent Awards
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Latest contracts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Major Contracts */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Major Active Contracts
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '2px solid #003366',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/cargo-systems-contract')}
            role="button"
            tabIndex={0}
            aria-label="View Cargo Systems O&M contract details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/cargo-systems-contract');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MoneyIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Cargo Systems O&M and Development
                </Typography>
              </Box>
              <Chip label="$184 Million" color="primary" sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                <strong>Award ID:</strong> 70B04C19F00000204
              </Typography>
              <Typography variant="body2" paragraph>
                Application development and operational support across CBP's Cargo Services Program Directorate.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Scope:</strong> Import/export processing systems</Typography></li>
                <li><Typography variant="body2"><strong>Focus:</strong> Compliance and trade facilitation</Typography></li>
                <li><Typography variant="body2"><strong>Services:</strong> Custom development, O&M support</Typography></li>
                <li><Typography variant="body2"><strong>Directorate:</strong> Cargo Services Program</Typography></li>
              </Box>
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed contract information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '2px solid #1976D2',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/ace-atap-enhancement-contract')}
            role="button"
            tabIndex={0}
            aria-label="View ACE & ATAP Enhancement contract details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/ace-atap-enhancement-contract');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AnalyticsIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  ACE & ATAP Enhancement (JV)
                </Typography>
              </Box>
              <Chip label="$136 Million" color="primary" sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                <strong>Joint Venture:</strong> Dev Technology Group + C. Mack Solutions
              </Typography>
              <Typography variant="body2" paragraph>
                Three-year task order to modernize and enhance ACE and ATAP with emerging technologies.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Duration:</strong> 3 years (2025-2028)</Typography></li>
                <li><Typography variant="body2"><strong>Focus:</strong> Emerging technologies, advanced analytics</Typography></li>
                <li><Typography variant="body2"><strong>Office:</strong> CBP Office of Information and Technology</Typography></li>
                <li><Typography variant="body2"><strong>Status:</strong> SBA-approved mentor status</Typography></li>
              </Box>
              <Typography variant="caption" sx={{ color: '#1976D2', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed contract information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '2px solid #2E7D32',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/ace-atap-bridge-contract')}
            role="button"
            tabIndex={0}
            aria-label="View ACE and ATAP Platforms Bridge Contract details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/ace-atap-bridge-contract');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  ACE and ATAP Platforms Bridge Contract
                </Typography>
              </Box>
              <Chip label="$43.8 Million" color="primary" sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                <strong>Award Date:</strong> October 2025
              </Typography>
              <Typography variant="body2" paragraph>
                Interim bridge task order for ACE and ATAP platforms support.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Type:</strong> Interim bridge task order</Typography></li>
                <li><Typography variant="body2"><strong>Services:</strong> Agile development, DevSecOps</Typography></li>
                <li><Typography variant="body2"><strong>Work:</strong> Legacy modernization, data management</Typography></li>
                <li><Typography variant="body2"><strong>Analytics:</strong> Expansion of analytics capabilities</Typography></li>
              </Box>
              <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed contract information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '2px solid #F57C00',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/dhs-ice-contracts')}
            role="button"
            tabIndex={0}
            aria-label="View Additional DHS/ICE Contracts details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/dhs-ice-contracts');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Additional DHS/ICE Contracts
                </Typography>
              </Box>
              <Chip label="$209 Million+" color="primary" sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                <strong>Portfolio:</strong> Identity, Biometrics, Student Systems
              </Typography>
              <Typography variant="body2" paragraph>
                Supporting ICE SEVP, biometrics, and collaboration services.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>SEVIS Support:</strong> $64M student/exchange visitor systems</Typography></li>
                <li><Typography variant="body2"><strong>Biometrics/IDM:</strong> $63M international student program</Typography></li>
                <li><Typography variant="body2"><strong>Collaboration:</strong> $82M ICE collaboration services</Typography></li>
                <li><Typography variant="body2"><strong>TSA FAST:</strong> $170M DHS TSA contract</Typography></li>
              </Box>
              <Typography variant="caption" sx={{ color: '#F57C00', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed contract information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Contract Details Table */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Contract Details & Timeline
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Contract</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Value</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Award Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Award ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Cargo Systems O&M</strong></TableCell>
              <TableCell>$184M</TableCell>
              <TableCell>2019</TableCell>
              <TableCell>70B04C19F00000204</TableCell>
              <TableCell><Chip label="Active" color="success" size="small" /></TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>ACE & ATAP Enhancement (JV)</strong></TableCell>
              <TableCell>$136M</TableCell>
              <TableCell>2025</TableCell>
              <TableCell>Recent Award</TableCell>
              <TableCell><Chip label="Active" color="success" size="small" /></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>ACE/ATAP Bridge</strong></TableCell>
              <TableCell>$43.8M</TableCell>
              <TableCell>Oct 2025</TableCell>
              <TableCell>Bridge Task Order</TableCell>
              <TableCell><Chip label="Active" color="success" size="small" /></TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>ICE SEVIS Support</strong></TableCell>
              <TableCell>$64M</TableCell>
              <TableCell>2025</TableCell>
              <TableCell>70B04C25F00000086</TableCell>
              <TableCell><Chip label="Active" color="success" size="small" /></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>ICE Biometrics/IDM</strong></TableCell>
              <TableCell>$63M</TableCell>
              <TableCell>2025</TableCell>
              <TableCell>Biometrics Contract</TableCell>
              <TableCell><Chip label="Active" color="success" size="small" /></TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>ICE Collaboration Services</strong></TableCell>
              <TableCell>$82M</TableCell>
              <TableCell>Recent</TableCell>
              <TableCell>Task Order</TableCell>
              <TableCell><Chip label="Active" color="success" size="small" /></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Core Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Core Capabilities & Services
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '2px solid #1976D2',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/application-development-capability')}
            role="button"
            tabIndex={0}
            aria-label="View Application Development capability details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/application-development-capability');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CodeIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Application Development
                </Typography>
              </Box>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Agile Development:</strong> Scrum, Kanban, SAFe methodologies</Typography></li>
                <li><Typography variant="body2"><strong>Custom Applications:</strong> Trade, cargo, identity systems</Typography></li>
                <li><Typography variant="body2"><strong>Full-Stack Development:</strong> Frontend, backend, database</Typography></li>
                <li><Typography variant="body2"><strong>API Development:</strong> RESTful, microservices architecture</Typography></li>
                <li><Typography variant="body2"><strong>Mobile Development:</strong> iOS, Android, cross-platform</Typography></li>
              </Box>
              <Typography variant="caption" sx={{ color: '#1976D2', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed capability information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '2px solid #2E7D32',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/modernization-devsecops-capability')}
            role="button"
            tabIndex={0}
            aria-label="View Modernization & DevSecOps capability details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/modernization-devsecops-capability');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Modernization & DevSecOps
                </Typography>
              </Box>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Legacy Modernization:</strong> Mainframe to cloud migration</Typography></li>
                <li><Typography variant="body2"><strong>DevSecOps:</strong> CI/CD pipelines, automated testing</Typography></li>
                <li><Typography variant="body2"><strong>Cloud Services:</strong> AWS, Azure, hybrid cloud</Typography></li>
                <li><Typography variant="body2"><strong>Containerization:</strong> Docker, Kubernetes orchestration</Typography></li>
                <li><Typography variant="body2"><strong>Infrastructure as Code:</strong> Terraform, CloudFormation</Typography></li>
              </Box>
              <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed capability information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '2px solid #F57C00',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/data-analytics-capability')}
            role="button"
            tabIndex={0}
            aria-label="View Data & Analytics capability details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/data-analytics-capability');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AnalyticsIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Data & Analytics
                </Typography>
              </Box>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Advanced Analytics:</strong> ATAP platform enhancement</Typography></li>
                <li><Typography variant="body2"><strong>Data Management:</strong> ETL, data warehousing, governance</Typography></li>
                <li><Typography variant="body2"><strong>Business Intelligence:</strong> Dashboards, reporting, visualization</Typography></li>
                <li><Typography variant="body2"><strong>Machine Learning:</strong> Predictive models, AI integration</Typography></li>
                <li><Typography variant="body2"><strong>Big Data:</strong> Hadoop, Spark, data lake architecture</Typography></li>
              </Box>
              <Typography variant="caption" sx={{ color: '#F57C00', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed capability information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '2px solid #7B1FA2',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/identity-biometrics-capability')}
            role="button"
            tabIndex={0}
            aria-label="View Identity & Biometrics capability details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/identity-biometrics-capability');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Identity & Biometrics
                </Typography>
              </Box>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Identity Management:</strong> IAM, authentication, authorization</Typography></li>
                <li><Typography variant="body2"><strong>Biometric Systems:</strong> Facial recognition, fingerprint matching</Typography></li>
                <li><Typography variant="body2"><strong>SEVIS Support:</strong> Student and exchange visitor systems</Typography></li>
                <li><Typography variant="body2"><strong>Access Control:</strong> RBAC, multi-factor authentication</Typography></li>
                <li><Typography variant="body2"><strong>Compliance:</strong> FISMA, NIST, DHS security standards</Typography></li>
              </Box>
              <Typography variant="caption" sx={{ color: '#7B1FA2', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed capability information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Supported Systems */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        CBP Systems Supported
      </Typography>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                Trade & Cargo Systems
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2"><strong>ACE:</strong> Automated Commercial Environment</Typography></li>
                <li><Typography variant="body2"><strong>ATAP:</strong> Advanced Trade Analytics Program</Typography></li>
                <li><Typography variant="body2"><strong>Cargo Systems:</strong> Import/export processing</Typography></li>
                <li><Typography variant="body2"><strong>Trade Compliance:</strong> Enforcement and facilitation</Typography></li>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
                Identity & Biometrics
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2"><strong>SEVIS:</strong> Student/Exchange Visitor Information</Typography></li>
                <li><Typography variant="body2"><strong>Biometric Systems:</strong> Identity verification</Typography></li>
                <li><Typography variant="body2"><strong>IDM:</strong> Identity management platforms</Typography></li>
                <li><Typography variant="body2"><strong>Access Control:</strong> Authentication systems</Typography></li>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                Analytics & Modernization
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2"><strong>ATAP Enhancement:</strong> Advanced analytics</Typography></li>
                <li><Typography variant="body2"><strong>Legacy Modernization:</strong> System upgrades</Typography></li>
                <li><Typography variant="body2"><strong>Data Management:</strong> Enterprise data platforms</Typography></li>
                <li><Typography variant="body2"><strong>Emerging Tech:</strong> AI/ML integration</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* References & Links */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Reference Links & Sources
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Contract Awards:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li>
                  <Link href="https://orangeslices.ai/dhs-cbp-awards-44m-ace-and-atap-platforms-application-development-and-om-support-bridge-contract/" target="_blank" rel="noopener">
                    <Typography variant="body2">ACE/ATAP Bridge Contract ($43.8M)</Typography>
                  </Link>
                </li>
                <li>
                  <Link href="https://orangeslices.ai/c-mack-solutions-and-dev-technology-group-jv-wins-136m-dhs-cbp-ace-advanced-trade-analytics-program-support-task/" target="_blank" rel="noopener">
                    <Typography variant="body2">ACE/ATAP Enhancement JV ($136M)</Typography>
                  </Link>
                </li>
                <li>
                  <Link href="https://www.usaspending.gov/award/CONT_AWD_70B04C19F00000204_7014_GS35F0897R_4730" target="_blank" rel="noopener">
                    <Typography variant="body2">Cargo Systems Contract ($184M)</Typography>
                  </Link>
                </li>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Additional Resources:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li>
                  <Link href="https://www.usaspending.gov/recipient/d768b60a-beaa-2e46-7aa5-6df63e232bb2-C/latest" target="_blank" rel="noopener">
                    <Typography variant="body2">USASpending.gov - Dev Technology Profile</Typography>
                  </Link>
                </li>
                <li>
                  <Link href="https://devtechnology.com/blog/" target="_blank" rel="noopener">
                    <Typography variant="body2">Dev Technology Group Blog</Typography>
                  </Link>
                </li>
                <li>
                  <Link href="https://www.federalcompass.com/federal-government-awarded-contracts/by-gov/US-Customs-and-Border-Protection-(CBP)" target="_blank" rel="noopener">
                    <Typography variant="body2">Federal Compass - CBP Contracts</Typography>
                  </Link>
                </li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default DevTechnologyContracts;
